import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import TopNav from "@/components/TopNav";
import { ArrowLeft, ArrowRight, CheckCircle2, Lock, PlayCircle } from "lucide-react";

function BlockRow({ b, courseId }) {
  const status = b.status;
  const isComplete = status === "complete";
  const isProg = status === "in_progress";
  const isLocked = status === "locked";
  const pill = isComplete ? "tw-pill-done" : isProg ? "tw-pill-prog" : "tw-pill-lock";
  const pillText = isComplete ? "Complete" : isProg ? "In progress" : status === "available" ? "Available" : "Locked";
  const numBg = isComplete ? {background:"var(--tawbti-green-bg)", color:"var(--tawbti-green-text)"} :
                 isProg ? {background:"var(--tawbti-blue-bg)", color:"var(--tawbti-blue-text)"} :
                          {background:"var(--tawbti-locked-bg)", color:"var(--tawbti-locked-text)"};
  const fillBg = isComplete ? "var(--tawbti-green-fill)" : "var(--tawbti-blue-fill)";
  const firstLesson = b.lessons_data?.[0];
  const target = isLocked ? "#" : `/courses/${courseId}/lessons/${firstLesson?.lesson_id || ''}`;
  return (
    <Link to={target} onClick={e=>isLocked && e.preventDefault()}
      className={`tw-card p-5 flex items-center gap-4 tw-card-hover ${isLocked?"opacity-70 cursor-not-allowed":""} ${isProg?"ring-1 ring-blue-200":""}`}
      data-testid={`block-row-${b.order}`}>
      <div className="tw-num-badge" style={numBg}>{b.order}</div>
      <div className="flex-1 min-w-0">
        <div className="font-bold tw-display text-base sm:text-lg leading-snug">Block {b.order} — {b.title}</div>
        <div className="text-xs text-stone-500 mt-1.5 truncate">Video · Slides · Reading · Quiz · {b.total_count} lesson{b.total_count>1?"s":""}</div>
      </div>
      <div className="flex flex-col items-end gap-2 min-w-[150px]">
        <span className={`tw-pill ${pill}`}>
          {isComplete && <CheckCircle2 size={11}/>}
          {isProg && <PlayCircle size={11}/>}
          {isLocked && <Lock size={11}/>}
          {pillText}
        </span>
        <div className="w-32">
          <div className="tw-progress-track"><div className="tw-progress-fill" style={{width:`${b.progress_pct}%`, background:fillBg}}/></div>
          <div className="text-[11px] text-stone-500 mt-1 text-right font-medium">{b.progress_pct}%</div>
        </div>
      </div>
    </Link>
  );
}

export default function CourseDetail() {
  const { courseId } = useParams();
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/courses/${courseId}`).then(r=>setData(r.data)).catch(()=>setData(false));
  }, [courseId]);

  if (data === null) return <div className="min-h-screen"><TopNav/><div className="p-10 text-stone-500" data-testid="course-loading">Loading course…</div></div>;
  if (!data) return <div className="min-h-screen"><TopNav/><div className="p-10 text-stone-500">Course not found</div></div>;

  const currentBlock = data.blocks.find(b => b.status === "in_progress") || data.blocks.find(b => b.status === "available") || data.blocks[0];

  return (
    <div className="min-h-screen pb-24">
      <TopNav/>
      <div className="max-w-7xl mx-auto px-6 py-8 fade-in">
        <Link to="/dashboard" className="text-sm text-stone-500 inline-flex items-center gap-1 hover:text-stone-800" data-testid="course-back-link"><ArrowLeft size={14}/> Back to dashboard</Link>

        <div className="mt-4 tw-mono-label" data-testid="course-code">{data.course.course_id} · {data.course.category || "Management"}</div>
        <h1 className="tw-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-1" data-testid="course-title">{data.course.title}</h1>
        <p className="text-stone-600 mt-2 max-w-2xl" data-testid="course-subtitle">{data.course.subtitle}</p>

        <section className="tw-card p-7 mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div className="flex-1">
            <div className="tw-mono-label">OVERALL PROGRESS</div>
            <div className="flex items-baseline gap-3 mt-1">
              <div className="tw-display text-4xl font-extrabold" data-testid="course-overall-pct">{data.overall_pct}%</div>
              <div className="text-sm text-stone-500">{data.blocks.filter(b=>b.status==="complete").length} of {data.blocks.length} blocks complete</div>
            </div>
            <div className="tw-progress-track mt-3 max-w-md"><div className="tw-progress-fill" style={{width:`${data.overall_pct}%`, background:"var(--tawbti-blue-fill)"}}/></div>
          </div>
          {currentBlock && (
            <button onClick={()=>{const l=currentBlock.lessons_data?.[0]; if(l) nav(`/courses/${courseId}/lessons/${l.lesson_id}`);}} className="tw-btn-primary inline-flex items-center gap-2 self-start" data-testid="resume-block-btn">
              {data.overall_pct === 0 ? "Start course" : "Resume"} <ArrowRight size={16}/>
            </button>
          )}
        </section>

        <div className="tw-mono-label mt-10 mb-3" data-testid="course-blocks-label">COURSE BLOCKS</div>
        <div className="space-y-3 stagger" data-testid="block-list">
          {data.blocks.map(b => <BlockRow key={b.block_id} b={b} courseId={courseId}/>)}
        </div>
      </div>
    </div>
  );
}
